import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatTranslation } from 'intl';
import { InspectionTemplateTranslationKeys } from 'intl/keys';
import { Box, Collapse, Button, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useIntl } from 'react-intl';
import { GraphicUploader } from 'components/common/graphicUploader';
import { ATTACHMENTS_UPLOADER_PRESET } from 'components/common/graphicUploader/attachmentsPreset';
import { useGraphicUploader } from 'components/common/graphicUploader/useLocalGraphicUploader';
import { useInspectionTemplate } from 'store/inspection';
import { useAppUrlParams, UrlParamName } from 'hooks/navigation/useAppUrlParams';
import { DocumentToSectionFromUrlBinder } from 'shared/domain/document/documentBinder';
import { createTemplateChecklistSectionDocumentsBaseUrl } from 'shared/domain/template/templateDocumentPaths';
import {
  MemoChecklistTreeNodeHeader,
  MAX_SECTION_DEPTH,
} from 'components/common/checklistTree';
import { MemoValidatedTextField } from 'components/common/forms/fields/ValidatedTextField';
import { RichTextEditor } from 'components/common/RichTextEditor';
import { CollapsibleRichTextComment } from 'components/inspection/CollapsibleRichTextComment';
import { InspectionCommentLabelVariant } from 'components/inspection/CollapsibleInspectionComment/types';
import { InspectionTranslationKeys } from 'intl/keys';
import { fieldLabelSx } from 'shared/styles/typography';
import {
  SectionContainer,
  SectionHeader,
  SectionBody,
  ChildrenWrapper,
  AddButtonsRow,
} from './styles';
import { SectionNodeProps } from './types';

export function SectionNode({
  node,
  onTitleChange,
  onDescriptionChange,
  onGraphicUploaderInit,
  onDelete,
  onAddItem,
  onAddSubsection,
  errors,
  defaultValues,
  graphicUploader,
  dragHandleProps,
  children,
}: SectionNodeProps): React.ReactElement {
  const intl = useIntl();
  const inspectionTemplateId = useAppUrlParams(
    UrlParamName.INSPECTION_TEMPLATE_ID
  );
  const { inspectionTemplateDocuments } = useInspectionTemplate();
  const [expanded, setExpanded] = useState(true);
  const inputPlaceholder = useMemo(
    () => formatTranslation(intl, InspectionTemplateTranslationKeys.wizardSectionPlaceholder),
    [intl]
  );
  const fallbackTitle = useMemo(
    () => formatTranslation(intl, InspectionTemplateTranslationKeys.wizardSectionDefaultTitle),
    [intl]
  );
  // The title goes up on every change and comes back as the node's data.
  const sectionTitle = node.data.title || '';
  const hasChildren = node.children.length > 0;
  const canAddNextItem = sectionTitle.trim().length > 0;
  const sectionDocuments = useMemo(
    () => inspectionTemplateDocuments.sectionDocuments[node.id] || [],
    [inspectionTemplateDocuments.sectionDocuments, node.id]
  );
  const localGraphicUploader = useGraphicUploader(sectionDocuments, {
    ...ATTACHMENTS_UPLOADER_PRESET,
    baseUrl: createTemplateChecklistSectionDocumentsBaseUrl(
      inspectionTemplateId,
      node.id
    ),
    atomicChanges: true,
    dataQaPrefix: `checklist-section-${node.id}-`,
    documentBinderFactory: (baseUrl?: string) => {
      if (!baseUrl) {
        throw new Error('Section uploader requires baseUrl');
      }
      return new DocumentToSectionFromUrlBinder(baseUrl);
    },
  });
  const graphicUploaderToUse = graphicUploader || localGraphicUploader;

  useEffect(() => {
    if (!graphicUploader && onGraphicUploaderInit) {
      onGraphicUploaderInit(node.id, localGraphicUploader);
    }
  }, [
    graphicUploader,
    onGraphicUploaderInit,
    node.id,
    localGraphicUploader,
  ]);

  const handleTitleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (onTitleChange) {
        onTitleChange(node.id, event.target.value);
      }
    },
    [node.id, onTitleChange]
  );

  const handleDescriptionChange = useCallback(
    (html: string) => {
      if (onDescriptionChange) {
        onDescriptionChange(node.id, html);
      }
    },
    [node.id, onDescriptionChange]
  );

  const handleDelete = useCallback(() => {
    if (onDelete) {
      onDelete(node.id);
    }
  }, [node.id, onDelete]);

  const handleAddItem = useCallback(() => {
    if (onAddItem) {
      onAddItem(node.id);
    }
  }, [node.id, onAddItem]);

  const handleAddSubsection = useCallback(() => {
    if (onAddSubsection) {
      onAddSubsection(node.id);
    }
  }, [node.id, onAddSubsection]);

  const handleExpandToggle = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  return (
    <SectionContainer elevation={0}>
      <SectionHeader>
        <MemoChecklistTreeNodeHeader
          displayNumber={node.displayNumber}
          title={sectionTitle || fallbackTitle}
          expanded={expanded}
          onExpandToggle={handleExpandToggle}
          onDelete={onDelete ? handleDelete : undefined}
          dragHandleProps={dragHandleProps}
          titleFontWeight={700}
        />
      </SectionHeader>

      <Collapse in={expanded}>
        <SectionBody>
          <Stack spacing={2}>
            <MemoValidatedTextField
              label={formatTranslation(intl, InspectionTemplateTranslationKeys.wizardSectionTitle)}
              placeholder={inputPlaceholder}
              variant="outlined"
              size="medium"
              multiline
              value={sectionTitle}
              onChange={handleTitleChange}
              required
              fieldError={errors?.title}
              fullWidth
              inputProps={{ maxLength: 500 }}
            />

            <Box>
              <Typography component='p' sx={fieldLabelSx}>
                {formatTranslation(
                  intl,
                  InspectionTemplateTranslationKeys.wizardSectionDescription
                )}
              </Typography>
              <CollapsibleRichTextComment
                value={node.data.description ?? ''}
                labelVariant={InspectionCommentLabelVariant.section}
              >
                <RichTextEditor
                  value={node.data.description ?? ''}
                  onChange={handleDescriptionChange}
                  error={Boolean(errors?.description)}
                  maxLength={10000}
                  minRows={3}
                  dense
                  placeholder={formatTranslation(
                    intl,
                    InspectionTranslationKeys.commentAddPlaceholder
                  )}
                />
              </CollapsibleRichTextComment>
            </Box>

            <GraphicUploader {...graphicUploaderToUse} />
          </Stack>

          {hasChildren && <ChildrenWrapper>{children}</ChildrenWrapper>}

          <AddButtonsRow>
            {onAddItem && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddItem}
                disabled={!canAddNextItem}
              >
                {formatTranslation(intl, InspectionTemplateTranslationKeys.wizardAddItem)}
              </Button>
            )}
            {onAddSubsection && node.depth < MAX_SECTION_DEPTH && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddSubsection}
                disabled={!canAddNextItem}
              >
                {formatTranslation(intl, InspectionTemplateTranslationKeys.wizardAddSection)}
              </Button>
            )}
          </AddButtonsRow>
        </SectionBody>
      </Collapse>
    </SectionContainer>
  );
}

export const MemoSectionNode = React.memo(SectionNode);
